"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.65, 0, 0.35, 1] as const;

const pen = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/**
 * 2×2 process sketch — viewBox 380×230, left padding added so the
 * return arc on the left side is never clipped.
 *
 *   [DISCOVER] ──► [PLAN]
 *        ▲            │
 *   [HANDOVER] ◄── [BUILD]
 *
 * Box centres (cx):  left=95  right=285   top cy=50  bottom cy=168
 * Box size: w=130 h=52
 *
 * Timing: the boxes and labels draw in once over ~1.1s. The arrows loop
 * independently from the moment the sketch is in view, redrawing one after
 * another for as long as it stays on screen. Each connector and its arrowhead
 * share one path, so the head lands with the line instead of popping in after.
 */

/** Box outline: draws its border, then settles. */
const box = (delay: number) => ({
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.38, ease: EASE, delay },
      opacity: { duration: 0.15, delay },
    },
  },
});

/**
 * Label: fades up as its box finishes drawing.
 * `to` is the resting opacity, so muted labels stay muted (the BUILD label
 * sits brighter against the mint fill).
 */
const label = (delay: number, to: number) => ({
  hidden: { opacity: 0, y: 4 },
  visible: {
    opacity: to,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" as const, delay },
  },
});

/* ── Looping arrow cycle ──────────────────────────────────────────────
   The four boxes draw once and then stay put. The arrows are what keeps
   moving: each one redraws itself in turn, holds while the rest of the lap
   plays out, then clears just before the cycle starts over. Nothing extra is
   overlaid on the diagram, so the only thing in motion is the arrows
   themselves. */

/** Seconds per lap. The boxes finish drawing ~0.9s in, well inside lap one. */
const LAP = 3.6;
/** Fraction of a lap one arrow spends drawing itself. */
const DRAW_FRAC = 0.15;
/** Fraction of a lap the whole circuit stays up before it wipes. */
const HOLD_UNTIL = 0.88;

/**
 * Looping connector. `order` is the arrow's position in the cycle (0-3): it
 * waits its turn, draws over DRAW_FRAC of the lap, holds while the remaining
 * arrows draw, then clears with the others so the next lap starts from empty.
 *
 * Every arrow starts at pathLength 0, including on the very first lap, so the
 * sketch is never already drawn when it scrolls into view.
 *
 * The first arrow starts at t=0 and so has no leading wait; the rest get an
 * extra keyframe holding them at zero until their turn. `times` must be
 * strictly increasing, which is why the two shapes differ.
 */
const arrowCycle = (order: number) => {
  const start = order * 0.22;
  const drawn = start + DRAW_FRAC;

  const waits = start > 0;
  return {
    pathLength: waits ? [0, 0, 1, 1, 0] : [0, 1, 1, 0],
    opacity: waits ? [0, 1, 1, 1, 0] : [1, 1, 1, 0],
    transition: {
      duration: LAP,
      repeat: Infinity,
      ease: "easeInOut" as const,
      times: waits
        ? [0, start, drawn, HOLD_UNTIL, 1]
        : [0, drawn, HOLD_UNTIL, 1],
    },
  };
};

/**
 * Arrows start empty and stay empty until the loop picks them up, so the
 * sketch is never sitting there pre-drawn when it scrolls into view.
 * Reduced motion is the one case that rests them fully drawn instead.
 */
const arrowInitial = { pathLength: 0, opacity: 0 };
const arrowRest = { pathLength: 1, opacity: 1 };

export const LoopSketch = ({ className }: { className?: string }) => {
  const reduce = useReducedMotion();
  const ref = useRef<SVGSVGElement>(null);

  // Drives the arrow loop. Unlike the one-shot box draw-in this tracks
  // visibility both ways, so the loop stops costing frames once it scrolls off.
  const inView = useInView(ref, { margin: "-80px" });

  // Reduced motion: render the finished sketch, no drawing, no looping.
  const animate = reduce ? undefined : "visible";
  const initial = reduce ? "visible" : "hidden";
  // The arrows loop from the moment the sketch is on screen. Lap one runs
  // alongside the boxes drawing themselves in, so nothing is ever sitting
  // there already finished when the section arrives.
  const looping = !reduce && inView;
  // Off screen the arrows park empty, ready to draw again on the way back.
  // Reduced motion parks them drawn, since they will never animate.
  const arrowIdle = reduce ? arrowRest : arrowInitial;

  return (
    <motion.svg
      ref={ref}
      viewBox="0 0 380 230"
      role="img"
      aria-label="How we work: Discover, Plan, Build, Handover."
      className={cn("block", className)}
      initial={initial}
      whileInView={animate}
      viewport={{ once: true, margin: "-80px" }}
    >
      {/* ── Box 1: DISCOVER (top-left) cx=95 cy=50 ────────────────── */}
      <motion.path
        d="M32 26 C55 22, 130 22, 158 26 C161 36, 160 62, 158 74 C130 78, 55 78, 32 74 C29 64, 30 36, 32 26 C32 26, 33 25, 34 25"
        {...pen}
        stroke="currentColor"
        strokeOpacity={0.7}
        strokeWidth={2.2}
        variants={box(0)}
      />

      {/* ── Arrow right: DISCOVER → PLAN (line + head, one stroke) ── */}
      <motion.path
        d="M164 50 C192 50, 210 50, 218 50 M210 43 C213 46, 217 49, 218 50 C214 52, 210 55, 208 58"
        {...pen}
        stroke="var(--brand-accent)"
        strokeWidth={2.5}
        initial={reduce ? arrowRest : arrowInitial}
        animate={looping ? arrowCycle(0) : arrowIdle}
      />

      {/* ── Box 2: PLAN (top-right) cx=285 cy=50 ─────────────────── */}
      <motion.path
        d="M222 26 C245 22, 320 22, 348 26 C351 36, 350 62, 348 74 C320 78, 245 78, 222 74 C219 64, 220 36, 222 26 C222 26, 223 25, 224 25"
        {...pen}
        stroke="currentColor"
        strokeOpacity={0.7}
        strokeWidth={2.2}
        variants={box(0.22)}
      />

      {/* ── Arrow down: PLAN → BUILD ───────────────────────────────── */}
      <motion.path
        d="M285 80 C285 108, 285 120, 285 136 M278 128 C281 132, 284 135, 285 136 C287 132, 290 128, 292 126"
        {...pen}
        stroke="var(--brand-accent)"
        strokeWidth={2.5}
        initial={reduce ? arrowRest : arrowInitial}
        animate={looping ? arrowCycle(1) : arrowIdle}
      />

      {/* ── Box 3: BUILD (bottom-right, mint) cx=285 cy=168 ──────── */}
      <motion.path
        d="M222 144 C245 140, 320 140, 348 144 C351 154, 350 180, 348 192 C320 196, 245 196, 222 192 C219 182, 220 154, 222 144 C222 144, 223 143, 224 143"
        {...pen}
        fill="var(--brand-mint)"
        stroke="currentColor"
        strokeOpacity={0.85}
        strokeWidth={2.2}
        variants={{
          hidden: { pathLength: 0, opacity: 0, fillOpacity: 0 },
          visible: {
            pathLength: 1,
            opacity: 1,
            fillOpacity: 1,
            transition: {
              pathLength: { duration: 0.38, ease: EASE, delay: 0.44 },
              opacity: { duration: 0.15, delay: 0.44 },
              // fill washes in behind the outline as it closes
              fillOpacity: { duration: 0.45, ease: "easeOut", delay: 0.62 },
            },
          },
        }}
      />

      {/* ── Arrow left: BUILD → HANDOVER ──────────────────────────── */}
      <motion.path
        d="M216 168 C194 168, 172 168, 162 168 M170 161 C167 164, 163 167, 162 168 C166 170, 170 173, 172 176"
        {...pen}
        stroke="var(--brand-accent)"
        strokeWidth={2.5}
        initial={reduce ? arrowRest : arrowInitial}
        animate={looping ? arrowCycle(2) : arrowIdle}
      />

      {/* ── Box 4: HANDOVER (bottom-left) cx=95 cy=168 ───────────── */}
      <motion.path
        d="M32 144 C55 140, 130 140, 158 144 C161 154, 160 180, 158 192 C130 196, 55 196, 32 192 C29 182, 30 154, 32 144 C32 144, 33 143, 34 143"
        {...pen}
        stroke="currentColor"
        strokeOpacity={0.7}
        strokeWidth={2.2}
        variants={box(0.66)}
      />

      {/* ── Return arc up: HANDOVER → DISCOVER (outside left edge) ── */}
      <motion.path
        d="M32 144 C16 144, 8 130, 8 110 C8 90, 8 70, 32 74 M24 68 C27 71, 30 73, 32 74 C30 77, 27 80, 26 83"
        {...pen}
        stroke="var(--brand-accent)"
        strokeWidth={2.5}
        initial={reduce ? arrowRest : arrowInitial}
        animate={looping ? arrowCycle(3) : arrowIdle}
      />

      {/* ── Labels: each appears with its own box ──────────────────── */}
      <g className="fill-current font-mono">
        <motion.text
          x="95"
          y="54"
          textAnchor="middle"
          fontSize="11"
          letterSpacing="0.11em"
          variants={label(0.18, 0.75)}
        >
          DISCOVER
        </motion.text>
        <motion.text
          x="285"
          y="54"
          textAnchor="middle"
          fontSize="11"
          letterSpacing="0.11em"
          variants={label(0.4, 0.75)}
        >
          PLAN
        </motion.text>
        <motion.text
          x="285"
          y="172"
          textAnchor="middle"
          fontSize="11"
          letterSpacing="0.11em"
          variants={label(0.62, 0.95)}
        >
          BUILD
        </motion.text>
        <motion.text
          x="95"
          y="172"
          textAnchor="middle"
          fontSize="11"
          letterSpacing="0.11em"
          variants={label(0.84, 0.75)}
        >
          HANDOVER
        </motion.text>
      </g>
    </motion.svg>
  );
};
